import { Box } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import tinycolorModule from 'tinycolor2';
import {
  ArtistThemeContext,
  ArtistThemeVariant,
} from '../contexts/ArtistThemeContext';
import useImageColor from './../hooks/useImageColor';
import useProcessColors from './../hooks/useProcessColors';
import { TypeOfWindowUndefined } from './_helpers';
import { safeGetItem, safeSetItem, safeSetJSON } from '../utils/safeStorage';
import { ARTIST_PAGE_SAMPLE, RELEASE_PAGE_SAMPLE } from '../utils/brandPalette';
import TopBarLoader from './loaders/TopBarLoader';

interface TinyColor {
  darken(amount: number): TinyColor;
  lighten(amount: number): TinyColor;
  getBrightness(): number;
  toHsl(): { h: number; s: number; l: number };
  toString(format?: string): string;
}

interface TinyColorStatic {
  (color: string | { h: number; s: number; l: number }): TinyColor;
  readability(color1: string, color2: string): number;
}

const tinycolor = tinycolorModule as unknown as TinyColorStatic;

const mostReadable = (backgroundColor: string, colors: string[]) => {
  return colors
    .map((color) => ({
      color,
      readability: tinycolor.readability(backgroundColor, color),
    }))
    .sort((a, b) => b.readability - a.readability)[0].color;
};

const themeSurfaceFromColor = (color: string, lightness: number) => {
  const hsl = tinycolor(color).toHsl();
  return tinycolor({
    h: hsl.h,
    s: Math.min(Math.max(hsl.s, 0.28), 0.72),
    l: lightness,
  }).toString('hex');
};

const buildThemeVariant = (
  id: string,
  label: string,
  baseStyle: Record<string, string>,
  seedColor: string
): ArtistThemeVariant => {
  const seed = tinycolor(seedColor);
  const bright = seed.getBrightness();
  const backgroundColor = themeSurfaceFromColor(seedColor, bright > 170 ? 0.86 : 0.12);
  const backgroundColor2 = themeSurfaceFromColor(seedColor, bright > 170 ? 0.8 : 0.08);
  const color5 = themeSurfaceFromColor(seedColor, bright > 170 ? 0.74 : 0.18);
  const logoColor =
    bright > 150 ? seed.darken(38).toString('hex') : seed.lighten(34).toString('hex');
  const logoColor2 =
    tinycolor.readability(backgroundColor, logoColor) >= 4.5
      ? logoColor
      : mostReadable(backgroundColor, [logoColor, '#ffffff', '#000000']);
  const textColor = mostReadable(backgroundColor, [logoColor, logoColor2, '#ffffff', '#000000']);
  const sidebarTextColor = mostReadable(color5, [logoColor, logoColor2, '#ffffff', '#000000']);
  const sidebarAccentColor = mostReadable(color5, [logoColor2, logoColor, '#ffffff', '#000000']);

  return {
    id,
    label,
    swatch: backgroundColor,
    accent: logoColor2,
    style: {
      ...baseStyle,
      '--color1': backgroundColor2,
      '--color2': seedColor,
      '--color5': color5,
      '--logoColor': logoColor,
      '--logoColor2': logoColor2,
      '--sidebarColor': color5,
      '--sidebarHeaderColor': textColor,
      '--sidebarTextColor': sidebarTextColor,
      '--sidebarAccentColor': sidebarAccentColor,
      '--sidebarSurfaceColor': backgroundColor2,
      '--sidebarSurfaceTextColor': textColor,
      '--textColor': textColor,
      '--backgroundColor': backgroundColor,
      '--backgroundColor2': backgroundColor2,
      '--scrollbarTrackColor': backgroundColor2,
      '--accentColor': seedColor,
    },
  };
};

const createThemeVariants = (
  combinedStyle?: Record<string, string>,
  hexColors?: string[]
): ArtistThemeVariant[] => {
  if (!combinedStyle || !hexColors?.length) return [];
  const colorDistance = (left: string, right: string) => {
    const leftHsl = tinycolor(left).toHsl();
    const rightHsl = tinycolor(right).toHsl();
    const hueDistance = Math.min(
      Math.abs(leftHsl.h - rightHsl.h),
      360 - Math.abs(leftHsl.h - rightHsl.h)
    ) / 180;
    return hueDistance * 0.62 + Math.abs(leftHsl.s - rightHsl.s) * 0.2 + Math.abs(leftHsl.l - rightHsl.l) * 0.18;
  };
  const candidates = hexColors
    .filter((color) => /^#[0-9a-f]{6}$/i.test(color))
    .filter(
      (color, index, self) =>
        self.findIndex((candidate) => candidate.toLowerCase() === color.toLowerCase()) === index
    );
  const primarySeed = candidates[0] || combinedStyle['--accentColor'] || combinedStyle['--logoColor'];
  const rankedSeeds = candidates
    .slice(1)
    .sort((left, right) => {
      const rightScore = tinycolor(right).toHsl().s + colorDistance(primarySeed, right);
      const leftScore = tinycolor(left).toHsl().s + colorDistance(primarySeed, left);
      return rightScore - leftScore;
    });
  const alternateSeeds: string[] = [];
  rankedSeeds.forEach((seed) => {
    if (
      alternateSeeds.length < 2 &&
      colorDistance(primarySeed, seed) >= 0.18 &&
      alternateSeeds.every((selected) => colorDistance(selected, seed) >= 0.18)
    ) {
      alternateSeeds.push(seed);
    }
  });
  const baseHsl = tinycolor(primarySeed).toHsl();
  const generatedOffsets = [72, 184, 288];
  for (const offset of generatedOffsets) {
    if (alternateSeeds.length >= 2) break;
    const generated = tinycolor({
      h: (baseHsl.h + offset) % 360,
      s: Math.min(0.78, Math.max(0.42, baseHsl.s + 0.18)),
      l: alternateSeeds.length === 0 ? 0.34 : 0.64,
    }).toString('hex');
    if (alternateSeeds.every((selected) => colorDistance(selected, generated) >= 0.2)) {
      alternateSeeds.push(generated);
    }
  }

  return [
    {
      id: 'primary',
      label: 'Original',
      swatch: combinedStyle['--backgroundColor'] || primarySeed,
      accent: combinedStyle['--logoColor2'] || combinedStyle['--logoColor'] || primarySeed,
      style: combinedStyle,
    },
    ...alternateSeeds.map((seed, index) =>
      buildThemeVariant(`image-${index + 1}`, `Image ${index + 1}`, combinedStyle, seed)
    ),
  ].slice(0, 3);
};

function Main(props) {
  const { artwork, children, sampleRegion = 'edges' } = props;
  const isFullArtworkSample = sampleRegion === 'full';
  const { colors } = useImageColor(artwork, {
    cors: true,
    ...(isFullArtworkSample ? RELEASE_PAGE_SAMPLE : ARTIST_PAGE_SAMPLE),
  });
  const { combinedStyle, hexColors } = useProcessColors(colors as string[] | undefined);
  const [style, setStyle] = useState({});
  const [selectedThemeId, setSelectedThemeId] = useState('primary');
  const themeVariants = useMemo(
    () => createThemeVariants(combinedStyle, hexColors),
    [combinedStyle, hexColors]
  );
  const activeTheme =
    themeVariants.find((theme) => theme.id === selectedThemeId) || themeVariants[0];

  useEffect(() => {
    setSelectedThemeId('primary');
  }, [artwork]);

  useEffect(() => {
    if (colors && colors.length && activeTheme?.style) {
      setStyle(activeTheme.style);
      if (!TypeOfWindowUndefined()) {
        safeSetJSON('colors', hexColors);
        safeSetJSON('processedStyles', activeTheme.style);
        safeSetItem('artistThemeId', activeTheme.id);
      }

      const root = document.querySelector(':root');
      if (root) {
        for (const [key, value] of Object.entries(activeTheme.style)) {
          (root as HTMLElement).style.setProperty(key, value);
        }
      }
    }
  }, [colors, activeTheme, hexColors]);

  useEffect(() => {
    if (!colors && !TypeOfWindowUndefined()) {
      const cachedStyles = safeGetItem('processedStyles');
      if (cachedStyles) {
        try {
          const parsedStyles = JSON.parse(cachedStyles) as Record<string, string>;
          const root = document.querySelector(':root');
          if (root) {
            for (const [key, value] of Object.entries(parsedStyles)) {
              (root as HTMLElement).style.setProperty(key, value);
            }
          }
        } catch (e) {
          console.error('Failed to restore cached styles:', e);
        }
      }
    }
  }, []);

  return (
    <ArtistThemeContext.Provider
      value={{ variants: themeVariants, selectedThemeId, setSelectedThemeId }}
    >
      <Box component="main" className="main" style={colors && colors.length ? style : {}}>
        {(!colors || !colors.length) && <TopBarLoader />}
        {children}
      </Box>
    </ArtistThemeContext.Provider>
  );
}

export default Main;
